import React, { useState } from "react";
import ThinkingBlock from "../ThinkingBlock";
import FileCard from "../FileCard";
import ClarificationCard from "../ClarificationCard";
import { ArtifactRef } from "../../types/contracts";
import {
  assetUrl,
  cleanArtifactFileName,
  fileNameFromUrl,
} from "../../config/runtime";

const imageMarkerRegex = () =>
  /\[Image:\s*((?:https?:\/\/|data:image\/|local:\/\/|\/?uploads\/)[^\s\]]+)\]/gi;

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5 15V6a2.5 2.5 0 0 1 2.5-2.5H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CodeBlock: React.FC<{ code: string; lang?: string }> = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    try {
      navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard not available */
    }
  };

  return (
    <div className="msg-codeblock">
      <div className="msg-codeblock__bar">
        <span className="msg-codeblock__lang">{lang || "code"}</span>
        <button type="button" className="msg-codeblock__copy" onClick={onCopy}>
          <CopyIcon />
          {copied ? "已复制" : "复制"}
        </button>
      </div>
      <pre className="msg-codeblock__pre">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const renderInline = (text: string, keyPrefix: string, seenUrls?: Set<string>): React.ReactNode[] => {
  // 正则拆分：代码块 `...`、加粗 **...**、图片 ![alt](url)、链接 [text](url)
  const regex = /(!?\[[^\]]*\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*)/g;
  const parts = text.split(regex);

  return parts
    .filter((p) => p !== "")
    .map((part, i) => {
      const key = `${keyPrefix}-i${i}`;

      // 1. 行内代码
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code key={key} className="msg-inline-code">
            {part.slice(1, -1)}
          </code>
        );
      }

      // 2. 粗体
      if (part.startsWith("**") && part.endsWith("**")) {
        const boldContent = part.slice(2, -2);

        // 模型常输出 **[下载文件](/uploads/...)**。外层粗体不能吞掉内层
        // Markdown 链接，否则文件会退化成纯文本；这里递归解析内部链接。
        if (/!?\[[^\]]+\]\([^)]+\)/.test(boldContent)) {
          return (
            <React.Fragment key={key}>
              {renderInline(boldContent, `${key}-inner`, seenUrls)}
            </React.Fragment>
          );
        }

        return <strong key={key}>{boldContent}</strong>;
      }

      // 3. Markdown 图片 ![alt](url)
      const imgMatch = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) {
        const alt = imgMatch[1] || "图片预览";
        // 后端产出的是相对路径 /uploads/xxx；开发模式下前端与 API 不同源，
        // 直接塞进 src 会打到前端 devServer 上 404，统一走 assetUrl 补全。
        const url = assetUrl(imgMatch[2]);
        if (seenUrls && seenUrls.has(url)) {
          return null; // 🛡️ 防重：同一消息中相同的图片 URL 仅渲染一次
        }
        seenUrls?.add(url);
        return (
          <div key={key} className="msg-image-wrap" style={{ margin: "10px 0" }}>
            <img
              src={url}
              alt={alt}
              style={{
                maxWidth: "100%",
                maxHeight: "360px",
                borderRadius: "14px",
                border: "1px solid var(--cl-border, #e5e3dc)",
                objectFit: "contain",
                display: "block",
                boxShadow: "0 4px 16px rgba(42, 39, 35, 0.06)",
              }}
            />
          </div>
        );
      }

      // 4. Markdown 超链接 [label](url) -> 识别为沙盒附件卡片 FileCard
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const label = linkMatch[1];
        const rawUrl = linkMatch[2];

        // 🛡️ 判定：如果 URL 指向 /uploads/ 或包含文件扩展名，渲染为 FileCard 附件卡片
        const isUploadFile =
          rawUrl.includes("/uploads/") ||
          rawUrl.startsWith("uploads/") ||
          /\.(png|jpg|jpeg|svg|txt|py|csv|json|md|zip|log)$/i.test(
            fileNameFromUrl(rawUrl)
          );

        if (isUploadFile) {
          // 相对路径补全成绝对地址，下载按钮的 fetch 才不会打到前端自身
          const url = assetUrl(rawUrl);
          if (seenUrls && seenUrls.has(url)) {
            return null; // 🛡️ 防重：同一消息中相同的文件链接仅渲染一次
          }
          seenUrls?.add(url);

          // 清理 label 前缀（如 "下载/查看原始文件 numbers.txt" -> "numbers.txt"）
          let cleanFileName = label
            .replace(/^(下载\/查看原始文件|生成的文件|下载|查看|\s)+/, "")
            .replace(/[`()]/g, "")
            .trim();
          if (
            !cleanFileName ||
            cleanFileName.length > 80 ||
            !/\.[a-z0-9]{1,10}$/i.test(cleanFileName)
          ) {
            cleanFileName = fileNameFromUrl(url);
          }
          cleanFileName = cleanArtifactFileName(cleanFileName, url);

          return <FileCard key={key} fileName={cleanFileName} fileUrl={url} />;
        }

        // 普通外链超链接
        return (
          <a
            key={key}
            href={rawUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#2563eb", textDecoration: "underline" }}
          >
            {label}
          </a>
        );
      }

      return <React.Fragment key={key}>{part}</React.Fragment>;
    });
};

const renderTableBlock = (tableLines: string[], keyPrefix: string): React.ReactNode => {
  if (tableLines.length < 2) {
    return tableLines.map((l, i) => (
      <p className="msg-text" key={`${keyPrefix}-tb-raw-${i}`}>
        {renderInline(l, `${keyPrefix}-tb-raw-${i}`)}
      </p>
    ));
  }

  const parseRow = (line: string) => {
    const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
    return trimmed.split("|").map((cell) => cell.trim());
  };

  const headers = parseRow(tableLines[0]);
  const isSeparator = /^\|?\s*:?-+:?\s*(\|?\s*:?-+:?\s*)+\|?$/.test(tableLines[1].trim());
  const dataRowStartIndex = isSeparator ? 2 : 1;
  const rows = tableLines.slice(dataRowStartIndex).map(parseRow);

  return (
    <div className="msg-table-wrap" key={keyPrefix}>
      <table className="msg-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={`${keyPrefix}-th-${i}`}>{renderInline(h, `${keyPrefix}-th-${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rIdx) => (
            <tr key={`${keyPrefix}-tr-${rIdx}`}>
              {row.map((cell, cIdx) => (
                <td key={`${keyPrefix}-td-${rIdx}-${cIdx}`}>
                  {renderInline(cell, `${keyPrefix}-td-${rIdx}-${cIdx}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderTextBlock = (
  text: string,
  keyPrefix: string,
  isStreaming: boolean = false,
  seenUrls?: Set<string>
): React.ReactNode => {
  const cleaned = text.replace(/^\n+|\n+$/g, "");
  if (cleaned === "" && !isStreaming) return null;

  // 🛡️ 判定：如果文本块包含可选选项，直接提取问题与按钮渲染 ClarificationCard
  if (cleaned.includes("可选选项")) {
    const qMatch = cleaned.match(/>\s*([\s\S]*?)(?=\n>\s*\*|\n>\s*-\s*`|\n>\s*\d+\.|\n*$)/);
    let questionText = qMatch ? qMatch[1].replace(/^>\s*/gm, "").trim() : "请选择以下选项以继续:";
    questionText = questionText.replace(/^\*\*.*?\*\*\s*\n?/, "").trim();
    
    const optionLines = cleaned.split("\n").filter(l => l.includes(">") && (/-\s*`/.test(l) || /^\s*>\s*\d+\./.test(l) || /^\s*>\s*-\s+/.test(l)));
    const options = optionLines.map(l => {
      let raw = l.replace(/^\s*>\s*(\d+\.|-)\s*/, "").replace(/^`/, "").replace(/`$/, "").trim();
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed.msg || parsed.option || parsed.text || raw;
        }
      } catch {
        /* ignore */
      }
      return raw;
    }).filter(Boolean);

    if (options.length > 0) {
      return (
        <ClarificationCard
          key={keyPrefix}
          question={questionText}
          options={options}
          onSelectOption={(opt) => {
            window.dispatchEvent(new CustomEvent("send_clarification_choice", { detail: opt }));
          }}
        />
      );
    }
  }

  const lines = cleaned.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const isLastLine = i === lines.length - 1;
    const lineKey = `${keyPrefix}-l${i}`;

    // 📍 1. 处理 Markdown 分割线 (--- 或 *** 或 ___)
    if (/^(---|\*\*\*|___)\s*$/.test(line.trim())) {
      blocks.push(<hr key={lineKey} className="msg-hr" />);
      i++;
      continue;
    }

    // 📍 2. 处理 Markdown 表格 (| col1 | col2 |)
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      blocks.push(renderTableBlock(tableLines, lineKey));
      continue;
    }

    // 📍 3. 处理无序列表 (- 或 *)
    if (/^[-*]\s+/.test(line)) {
      const listContent = line.replace(/^[-*]\s+/, "");
      blocks.push(
        <div className="msg-list-item msg-list-item--bullet" key={lineKey}>
          <span className="msg-list-dot">•</span>
          <span className="msg-list-text">{renderInline(listContent, lineKey, seenUrls)}</span>
          {isLastLine && isStreaming && <span className="msg-streaming-cursor">▋</span>}
        </div>
      );
      i++;
      continue;
    }

    // 📍 4. 处理有序列表 (1. 2. 3.)
    const numMatch = line.match(/^(\d+)\.\s+/);
    if (numMatch) {
      const listContent = line.replace(/^(\d+)\.\s+/, "");
      blocks.push(
        <div className="msg-list-item msg-list-item--number" key={lineKey}>
          <span className="msg-list-num">{numMatch[1]}.</span>
          <span className="msg-list-text">{renderInline(listContent, lineKey, seenUrls)}</span>
          {isLastLine && isStreaming && <span className="msg-streaming-cursor">▋</span>}
        </div>
      );
      i++;
      continue;
    }

    // 📍 5. 处理引用块 (> )
    if (/^>\s+/.test(line)) {
      const quoteContent = line.replace(/^>\s+/, "");
      const isToolStatus = quoteContent.includes("正在");
      blocks.push(
        <blockquote className={`msg-blockquote ${isToolStatus ? "msg-blockquote--tool-status" : ""}`} key={lineKey}>
          {isToolStatus && (
            <span className="msg-tool-spinner" style={{ marginRight: "8px", display: "inline-flex", alignItems: "center" }}>
              <svg className="animate-spin-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
              </svg>
            </span>
          )}
          {renderInline(quoteContent, lineKey, seenUrls)}
          {isLastLine && isStreaming && <span className="msg-streaming-cursor">▋</span>}
        </blockquote>
      );
      i++;
      continue;
    }

    // 📍 6. 处理标题 (# ## ### ####)
    const headMatch = line.match(/^(#{1,4})\s+/);
    if (headMatch) {
      const level = headMatch[1].length;
      const titleContent = line.replace(/^(#{1,4})\s+/, "");
      const HeadingTag = `h${Math.min(level + 2, 6)}` as keyof JSX.IntrinsicElements;
      blocks.push(
        <HeadingTag className={`msg-heading msg-heading--h${level}`} key={lineKey}>
          {renderInline(titleContent, lineKey, seenUrls)}
          {isLastLine && isStreaming && <span className="msg-streaming-cursor">▋</span>}
        </HeadingTag>
      );
      i++;
      continue;
    }

    // 📍 7. 普通文本段落
    blocks.push(
      <p className="msg-text" key={lineKey}>
        {renderInline(line, lineKey, seenUrls)}
        {isLastLine && isStreaming && <span className="msg-streaming-cursor">▋</span>}
      </p>
    );
    i++;
  }

  return (
    <div className="msg-text-block" key={keyPrefix}>
      {blocks}
    </div>
  );
};


interface MessageContentProps {
  text: string;
  isStreaming?: boolean;
  artifacts?: ArtifactRef[];
}

const MessageContent: React.FC<MessageContentProps> = ({ text, isStreaming = false, artifacts }) => {
  const raw = text ?? "";

  // 📍 1. 提取 <think>...</think> 思考块 (包含未完成闭合的 <think> 状态)
  let thinkText = "";
  let bodyText = raw;
  let isStillThinking = false;

  if (raw.includes("<think>")) {
    if (raw.includes("</think>")) {
      const thinkMatch = raw.match(/<think>([\s\S]*?)<\/think>/i);
      if (thinkMatch) {
        thinkText = thinkMatch[1].trim();
        bodyText = raw.replace(/<think>[\s\S]*?<\/think>/i, "").trim();
      }
    } else {
      // 📍 思考中 (未闭合 </think>)
      thinkText = raw.replace(/<think>/i, "").trim();
      bodyText = "";
      isStillThinking = true;
    }
  }

  // 📍 1.5 提取 <subagent>...</subagent> 专有子 Agent 过程块
  let subagentText = "";
  let isSubagentRunning = false;

  if (bodyText.includes("<subagent>")) {
    if (bodyText.includes("</subagent>")) {
      const subMatch = bodyText.match(/<subagent>([\s\S]*?)<\/subagent>/i);
      if (subMatch) {
        subagentText = subMatch[1].trim();
        bodyText = bodyText.replace(/<subagent>[\s\S]*?<\/subagent>/i, "").trim();
      }
    } else {
      // 📍 子 Agent 执行中 (未闭合 </subagent>)
      const parts = bodyText.split(/<subagent>/i);
      subagentText = parts[parts.length - 1].trim();
      bodyText = parts[0].trim();
      isSubagentRunning = true;
    }
  }

  // 📍 2. 提取所有 [Image: url/dataUrl] 多模态图片块
  const attachedImageUrls: string[] = [];
  const imgRegex = imageMarkerRegex();
  let m: RegExpExecArray | null;
  while ((m = imgRegex.exec(bodyText)) !== null) {
    attachedImageUrls.push(assetUrl(m[1]));
  }
  bodyText = bodyText.replace(imageMarkerRegex(), "").trim();

  // 去重只在同一个展示区内生效：用户附图、子 Agent 思考区和主模型正文
  // 是三个独立区域。子 Agent 先输出的 URL 不能屏蔽主模型的最终结果。
  const mainBodySeenUrls = new Set<string>();

  const nodes: React.ReactNode[] = [];
  const fence = /```(\w*)\n?([\s\S]*?)```/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let idx = 0;

  while ((match = fence.exec(bodyText)) !== null) {
    if (match.index > last) {
      nodes.push(renderTextBlock(bodyText.slice(last, match.index), `t${idx}`, false, mainBodySeenUrls));
    }
    nodes.push(<CodeBlock key={`c${idx}`} lang={match[1]} code={match[2].replace(/\n$/, "")} />);
    last = fence.lastIndex;
    idx += 1;
  }

  if (last <= bodyText.length) {
    nodes.push(renderTextBlock(bodyText.slice(last), `t${idx}`, isStreaming, mainBodySeenUrls));
  }

  // 🛡️ 防重：artifacts 是服务端从同一段正文里提取出来的结构化附件，与上面内联
  // [文件名](/uploads/xxx) 渲染出的 FileCard 往往是同一批文件。seenUrls 记的是内联
  // 时的原始 URL，两边可能一个相对一个绝对，统一归一化后再比，否则同一个文件会出现两张卡片。
  // artifacts 可能同时收集到子 Agent 和主模型正文中的同一 URL。
  // 这里只和主模型正文的 URL 去重，主模型正文拥有最终展示优先级。
  const renderedMainBodyUrls = new Set(
    Array.from(mainBodySeenUrls).map((u) => assetUrl(u))
  );
  const extraArtifacts = (artifacts || [])
    .filter((art) => art.url && !renderedMainBodyUrls.has(assetUrl(art.url)))
    // 服务端的 name 是落盘名 gen_ab12cd_report.md，去掉防重名前缀再展示，
    // 它同时是 FileCard 下载时用的文件名。
    .map((art) => ({
      ...art,
      name: cleanArtifactFileName(art.name, art.url),
    }));

  return (
    <div className="msg-content">
      {thinkText || isStillThinking ? (
        <ThinkingBlock thinkingText={thinkText} isThinking={isStillThinking} />
      ) : null}

      {subagentText || isSubagentRunning ? (
        <ThinkingBlock
          title="正在委派专有子 Agent 深度处理..."
          thinkingText={subagentText}
          isThinking={isSubagentRunning}
        />
      ) : null}

      {attachedImageUrls.length > 0 && (
        <div className="msg-image-wrap-container" style={{ marginBottom: "12px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {attachedImageUrls.map((u, i) => (
            <div key={`user-attached-img-${i}`} className="msg-image-wrap">
              <img
                src={u}
                alt={`用户附带图片 ${i + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "320px",
                  borderRadius: "16px",
                  border: "1px solid var(--cl-border)",
                  objectFit: "contain",
                  display: "block",
                  boxShadow: "0 4px 16px rgba(42, 39, 35, 0.06)",
                }}
              />
            </div>
          ))}
        </div>
      )}
      {nodes}
      {extraArtifacts.length > 0 && (
        <div className="msg-artifacts-wrap" style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
          {extraArtifacts.map((art, idx) => (
            <FileCard key={`art-${idx}`} fileName={art.name} fileUrl={assetUrl(art.url)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageContent;
